/**
 * Open-Meteo počasí pro stavební deník.
 *
 * Pro vybraný den (YYYY-MM-DD) vytáhne reálné počasí:
 *   - dnešek / budoucnost → endpoint /v1/forecast (current/hourly)
 *   - minulost (do ~5 dní zpět) → /v1/forecast s past_days
 *   - dál v minulosti → /v1/archive (ERA5 archiv)
 *
 * API je zdarma a bez klíče (https://open-meteo.com/).
 *
 * WMO weather_code mapování → naše kategorie počasí (jasno, polojasno, …).
 */

const WMO_TO_PODMINKY: Record<number, string> = {
  0: 'jasno',
  1: 'jasno',
  2: 'polojasno',
  3: 'zatazeno',
  45: 'zatazeno', 48: 'zatazeno',
  51: 'dest', 53: 'dest', 55: 'dest',
  56: 'mraz', 57: 'mraz',
  61: 'dest', 63: 'dest', 65: 'dest',
  66: 'mraz', 67: 'mraz',
  71: 'snih', 73: 'snih', 75: 'snih', 77: 'snih',
  80: 'dest', 81: 'dest', 82: 'dest',
  85: 'snih', 86: 'snih',
  95: 'bourkovy', 96: 'bourkovy', 99: 'bourkovy',
};

export function podminkyFromWmo(code: number | undefined): string {
  if (code == null) return '';
  return WMO_TO_PODMINKY[code] ?? '';
}

export function vitrFromKmh(kmh: number | undefined): string {
  if (kmh == null) return '';
  if (kmh < 10) return 'slabý';
  if (kmh < 30) return 'mírný';
  if (kmh < 50) return 'silný';
  return 'velmi silný';
}

export interface WeatherSnapshot {
  teplota: string;       // °C, zaokrouhleno
  podminky: string;      // klíč z PODMINKY (jasno, polojasno…)
  vitr: string;          // klíč z VITR (slabý, mírný…)
  source: 'forecast-current' | 'forecast-daily' | 'archive-daily';
}

/** Den dnes/budoucnost vs minulost — jednoduchá kategorie. */
function classifyDay(iso: string): 'today' | 'past-recent' | 'past-far' | 'future' {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(iso + 'T00:00:00');
  const diffDays = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'today';
  if (diffDays > 0) return 'future';
  if (diffDays >= -5) return 'past-recent';
  return 'past-far';
}

/**
 * Stáhne počasí pro daný den a souřadnice.
 * Vrátí null pokud se nepodařilo (síťová chyba, neexistující data).
 */
export async function fetchWeather(
  latitude: number,
  longitude: number,
  isoDate: string,
): Promise<WeatherSnapshot | null> {
  const tz = 'Europe/Prague';
  const cls = classifyDay(isoDate);

  try {
    if (cls === 'today') {
      // Aktuální měření
      const url =
        `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}` +
        `&current=temperature_2m,weather_code,wind_speed_10m&timezone=${encodeURIComponent(tz)}`;
      const r = await fetch(url);
      if (!r.ok) return null;
      const j = await r.json();
      const cur = j.current;
      if (!cur) return null;
      return {
        teplota: String(Math.round(Number(cur.temperature_2m))),
        podminky: podminkyFromWmo(Number(cur.weather_code)),
        vitr: vitrFromKmh(Number(cur.wind_speed_10m)),
        source: 'forecast-current',
      };
    }

    if (cls === 'past-recent' || cls === 'future') {
      // Použijeme /forecast s daily a explicitním datem (start_date/end_date)
      const url =
        `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}` +
        `&daily=temperature_2m_mean,weather_code,wind_speed_10m_max` +
        `&start_date=${isoDate}&end_date=${isoDate}` +
        `&timezone=${encodeURIComponent(tz)}`;
      const r = await fetch(url);
      if (!r.ok) return null;
      const j = await r.json();
      const d = j.daily;
      if (!d || !Array.isArray(d.time) || d.time.length === 0) return null;
      const t = Number(d.temperature_2m_mean?.[0]);
      const code = Number(d.weather_code?.[0]);
      const w = Number(d.wind_speed_10m_max?.[0]);
      return {
        teplota: Number.isFinite(t) ? String(Math.round(t)) : '',
        podminky: podminkyFromWmo(code),
        vitr: vitrFromKmh(w),
        source: 'forecast-daily',
      };
    }

    // past-far → archiv (ERA5)
    const url =
      `https://archive-api.open-meteo.com/v1/archive?latitude=${latitude}&longitude=${longitude}` +
      `&daily=temperature_2m_mean,weather_code,wind_speed_10m_max` +
      `&start_date=${isoDate}&end_date=${isoDate}` +
      `&timezone=${encodeURIComponent(tz)}`;
    const r = await fetch(url);
    if (!r.ok) return null;
    const j = await r.json();
    const d = j.daily;
    if (!d || !Array.isArray(d.time) || d.time.length === 0) return null;
    const t = Number(d.temperature_2m_mean?.[0]);
    const code = Number(d.weather_code?.[0]);
    const w = Number(d.wind_speed_10m_max?.[0]);
    return {
      teplota: Number.isFinite(t) ? String(Math.round(t)) : '',
      podminky: podminkyFromWmo(code),
      vitr: vitrFromKmh(w),
      source: 'archive-daily',
    };
  } catch (e) {
    console.warn('Open-Meteo fetch failed:', e);
    return null;
  }
}
